import type { OrgMember } from '../../../hooks/useOrgMembers';
import {
  mapRoleTypeToCargo,
  resolveLstCargoFromContext,
  type LstCargo,
  type LstTemplate,
} from '../types';

export type LstTemplateScope = 'cargo' | 'user';

export const LST_TEMPLATE_SCOPE_LABELS: Record<LstTemplateScope, string> = {
  cargo: 'Cargo',
  user: 'Individual',
};

export function resolveTemplateScope(template: Pick<LstTemplate, 'scope'>): LstTemplateScope {
  return template.scope === 'user' ? 'user' : 'cargo';
}

export function isUserScopedTemplate(
  template: Pick<LstTemplate, 'scope' | 'assigned_user_id'>,
): boolean {
  return resolveTemplateScope(template) === 'user' && !!template.assigned_user_id;
}

/** Rotina aplicável à agenda de um usuário específico (cargo + exceções individuais). */
export function templateAppliesToUser(
  template: LstTemplate,
  cargo: LstCargo,
  userId: string,
): boolean {
  if (!template.active || template.target_role !== cargo) return false;
  if (isUserScopedTemplate(template)) {
    return template.assigned_user_id === userId;
  }
  return true;
}

export function filterTemplatesForUser(
  templates: LstTemplate[],
  cargo: LstCargo,
  userId: string,
): LstTemplate[] {
  return templates.filter((t) => templateAppliesToUser(t, cargo, userId));
}

export type MemberRoleNameResolver = (member: OrgMember) => string | null | undefined;

export function buildRoleNameByRoleId(
  roles: Array<{ id: string; name: string }>,
): Map<string, string> {
  return new Map(roles.map((r) => [r.id, r.name]));
}

export function createMemberRoleNameResolver(
  roleNameByRoleId: Map<string, string>,
): MemberRoleNameResolver {
  return (member) => (member.roleId ? roleNameByRoleId.get(member.roleId) : undefined);
}

/** Cargo LST efetivo do membro — refina supervisor/coordenador pelo nome do papel. */
export function resolveMemberLstCargo(
  member: OrgMember,
  resolveRoleName?: MemberRoleNameResolver,
): LstCargo {
  const base = mapRoleTypeToCargo(member.roleType);
  return resolveLstCargoFromContext(base, resolveRoleName?.(member));
}

export function memberMatchesLstCargo(
  member: OrgMember,
  cargo: LstCargo,
  resolveRoleName?: MemberRoleNameResolver,
): boolean {
  return resolveMemberLstCargo(member, resolveRoleName) === cargo;
}

/** Membros elegíveis para exceção individual no setor/cargo selecionado. */
export function filterMembersForTemplateScope(
  members: OrgMember[],
  cargo: LstCargo,
  departmentId?: string,
  resolveRoleName?: MemberRoleNameResolver,
): OrgMember[] {
  return members
    .filter((m) => memberMatchesLstCargo(m, cargo, resolveRoleName))
    .filter((m) => !departmentId || m.departmentId === departmentId)
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
}

/** Listagem do cadastro — rotinas do cargo + exceções filtradas por setor/usuário. */
export function filterTemplatesForConfigScope(
  templates: LstTemplate[],
  cargo: LstCargo,
  options?: { departmentId?: string; members?: OrgMember[]; contextUserId?: string },
): LstTemplate[] {
  const departmentId = options?.departmentId;
  const members = options?.members ?? [];
  const contextUserId = options?.contextUserId;

  return templates.filter((t) => {
    if (!t.active || t.target_role !== cargo) return false;
    if (contextUserId) {
      return templateAppliesToUser(t, cargo, contextUserId);
    }
    if (!isUserScopedTemplate(t)) return true;
    if (!departmentId) return true;
    const member = members.find((m) => m.id === t.assigned_user_id);
    return member?.departmentId === departmentId;
  });
}

export function countTemplatesByScope(templates: LstTemplate[]): {
  cargo: number;
  individual: number;
  total: number;
} {
  let cargo = 0;
  let individual = 0;
  for (const t of templates) {
    if (isUserScopedTemplate(t)) individual += 1;
    else cargo += 1;
  }
  return { cargo, individual, total: templates.length };
}
